import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";

import { typeScale } from "../theme/tokens";

import { openInNewTab } from "./embed-utils";

import type { CSSProperties, ReactNode } from "react";

// The room's logo strip, shared by the marketplace grid ("Where we sell") and
// the client brand wall ("Brands that trust Bison") since 2026-10-26: the
// brand's Logo wall pattern (logos on plain white, no cards, no outlines,
// hover drops to 70 percent) built like the website's MarketplaceStrip
// (components/sections/MarketplaceStrip.tsx + LogoBand.tsx). Every logo sits
// in an equal-width cell, fitted (object-contain) into a mark box well inside
// it, and the rows are balanced to the column width so the last row never
// strands one logo where avoidable.

// The heading block both strips open with: the title, then the description
// at the lead size.
export function LogoStripHeading({ title, description }: { title?: string; description?: string }) {
    return (
        <>
            {title && (
                <Typography variant="h4" sx={{ mb: description ? 0.5 : 0 }}>
                    {title}
                </Typography>
            )}
            {description && (
                <Typography variant="body1" sx={{ color: "text.secondary", ...typeScale.lead, mb: 3, maxWidth: 1064 }}>
                    {description}
                </Typography>
            )}
        </>
    );
}

// Cell and gap sizes. Each mark is fitted into a box well inside its cell
// (Bill's reference strip, 2026-10-26: a 130px pitch, marks at most about
// 100 x 28px), so even the widest wordmarks keep about 30px of clear space
// between them and rows sit about 40px apart; marks that filled their cells
// read as jitter. 116px cells and 20px gaps from sm, 96px and 12px on a phone
// so three fit a row. In a narrow column (the Welcome page's left column)
// cells shrink to MIN_CELL rather than stack one logo per row.
const CELL = { xs: 96, sm: 116 } as const;
const MIN_CELL = 96;
const COL_GAP = { xs: 12, sm: 20 } as const;
const ROW_GAP = { xs: 28, sm: 40 } as const;
// The mark's box inside the cell: widest and tallest a logo may draw.
const MARK_WIDTH = { xs: 78, sm: 100 } as const;
const MARK_HEIGHT = { xs: 22, sm: 26, md: 28 } as const;

export function LogoGrid({ count, children }: { count: number; children: ReactNode }) {
    const theme = useTheme();
    const wide = useMediaQuery(theme.breakpoints.up("sm"));
    const cell = wide ? CELL.sm : CELL.xs;
    const gap = wide ? COL_GAP.sm : COL_GAP.xs;
    const ref = useRef<HTMLDivElement>(null);
    const [available, setAvailable] = useState(0);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => setAvailable(entry.contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Balance the rows: as many rows as the width forces, then the fewest
    // columns that still fill them, so 16 logos read 4 x 4 or 6 / 6 / 4,
    // never 5 / 5 / 5 / 1. The partial row centres under the full ones.
    const perRow = (w: number) => Math.floor((available + gap) / (w + gap));
    const fit = Math.max(1, perRow(cell) >= 3 ? perRow(cell) : Math.min(3, Math.max(perRow(cell), perRow(MIN_CELL))));
    const rows = Math.ceil(count / fit);
    const cols = Math.ceil(count / rows);
    const cellWidth = Math.min(cell, Math.floor((available - (cols - 1) * gap) / cols));
    const measured = available > 0;
    const maxWidth = measured ? cols * cellWidth + (cols - 1) * gap : undefined;

    return (
        // Breathing room above and below the block (Bill, 2026-10-26): 48px
        // from sm, 32px on a phone, on top of the description's 24px and the
        // panel's own spacing below.
        <Box ref={ref} sx={{ py: { xs: 4, sm: 6 } }}>
            <Box
                style={measured ? ({ "--logo-cell": `${cellWidth}px` } as CSSProperties) : undefined}
                sx={{
                    // Until the column is measured, cells take the breakpoint width.
                    "--logo-cell": { xs: `${CELL.xs}px`, sm: `${CELL.sm}px` },
                    mx: "auto",
                    maxWidth,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                    columnGap: { xs: `${COL_GAP.xs}px`, sm: `${COL_GAP.sm}px` },
                    rowGap: { xs: `${ROW_GAP.xs}px`, sm: `${ROW_GAP.sm}px` },
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

export function LogoTile({
    name,
    logoUrl,
    color,
    href,
    scale = 1,
    fallbackText = name.toLowerCase(),
}: {
    name: string;
    logoUrl?: string;
    /** Color of the typographic fallback when there is no logo file. */
    color: string;
    href?: string;
    /** Height multiplier; the width cap is shared by every mark. */
    scale?: number;
    /** Text of the typographic fallback (marketplaces read lowercase). */
    fallbackText?: string;
}) {
    // Open from script so a stripped target attribute cannot navigate the
    // room away (CEO report, 2026-09-04).
    const linkProps = href
        ? {
              component: "a" as const,
              href,
              target: "_blank",
              rel: "noopener noreferrer",
              onClick: openInNewTab(href),
          }
        : {};
    const height = {
        xs: Math.round(MARK_HEIGHT.xs * scale),
        sm: Math.round(MARK_HEIGHT.sm * scale),
        md: Math.round(MARK_HEIGHT.md * scale),
    };
    return (
        <Box
            {...linkProps}
            title={href ? name : undefined}
            sx={{
                // One equal cell per logo; the mark is fitted inside it.
                width: "var(--logo-cell)",
                maxWidth: "100%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textDecoration: "none",
                ...(href && {
                    transition: "opacity 150ms ease",
                    "&:hover": { opacity: 0.72 },
                }),
            }}
        >
            {logoUrl ? (
                <Box
                    component="img"
                    src={logoUrl}
                    alt={name}
                    loading="lazy"
                    sx={{
                        height,
                        width: "100%",
                        // The scale tunes height only: a wide mark stops at the
                        // common width, as in the reference strip.
                        maxWidth: { xs: MARK_WIDTH.xs, sm: MARK_WIDTH.sm },
                        objectFit: "contain",
                        display: "block",
                        // Drops the white ground of a JPG mark (Mathis Home)
                        // on any tinted ground; no change on white.
                        mixBlendMode: "multiply",
                    }}
                />
            ) : (
                <Typography
                    sx={{
                        color,
                        fontWeight: 800,
                        fontSize: { xs: "1rem", md: "1.125rem" },
                        letterSpacing: "-0.01em",
                        lineHeight: 1.1,
                    }}
                >
                    {fallbackText}
                </Typography>
            )}
        </Box>
    );
}
